import * as THREE from 'three';
import { Product } from '@/config/products';

// --- Specific Material Strategies ---

/**
 * Applies realistic glass material properties
 */
export const applyGlassMaterial = (
    child: THREE.Mesh,
    product: Product | undefined
) => {
    // Only apply if it's the "Glass Award" or similar product asking for glass
    const isGlassAward = !!product?.materialConfig?.useGlassMaterial;
    if (!isGlassAward) return;

    const partNameLower = child.name.toLowerCase();

    // Check exclusion list
    if (partNameLower.includes('base') || partNameLower.includes('wood')) return;

    // Check inclusion list
    const isGlassPart = partNameLower.includes('glass') ||
        partNameLower.includes('award') ||
        partNameLower.includes('crystal') ||
        partNameLower.includes('top');

    if (isGlassPart) {
        const applyProps = (mat: any) => {
            mat.transparent = true;
            mat.opacity = 0.6;
            mat.metalness = 0.1;
            mat.roughness = 0.1;
            mat.envMapIntensity = 2.0;
            mat.transmission = 0.0;
            mat.depthWrite = false;
        };

        if (Array.isArray(child.material)) {
            child.material.forEach(m => applyProps(m));
        } else {
            applyProps(child.material);
        }
    }
};

/**
 * Applies Chrome (Metallic) or Rubber (Grip) materials to Pen parts
 */
export const applyPenMaterials = (
    child: THREE.Mesh,
    product: Product | undefined,
    glbUrl: string | null
) => {
    const isPen = product?.id?.includes('pen') || glbUrl?.toLowerCase().includes('pen') || product?.id === 'square-pen';
    if (!isPen) return;

    const lowerName = child.name.toLowerCase();

    // 1. Chrome Parts (Clip, Top, Tip)
    const isChromePart = (lowerName.includes('clip') || lowerName.includes('tip') || lowerName.includes('top') || lowerName.includes('nib')) && !lowerName.includes('body');

    if (isChromePart) {
        const applyChrome = (mat: any) => {
            const newMat = mat.clone();
            newMat.metalness = 1.0;
            newMat.roughness = 0.05;
            newMat.color.setHex(0xffffff);
            newMat.transparent = false;
            newMat.opacity = 1.0;
            newMat.envMapIntensity = 3.0;
            newMat.side = THREE.DoubleSide;
            newMat.depthWrite = true;
            newMat.depthTest = true;
            return newMat;
        };

        if (Array.isArray(child.material)) {
            child.material = child.material.map(m => applyChrome(m));
        } else {
            // @ts-ignore
            child.material = applyChrome(child.material);
        }
    }
    // 2. Grip Parts
    else if (lowerName.includes('grip')) {
        const applyRubber = (mat: any) => {
            const newMat = mat.clone();
            newMat.transparent = false;
            newMat.opacity = 1.0;
            newMat.side = THREE.DoubleSide;
            newMat.depthWrite = true;
            newMat.depthTest = true;
            return newMat;
        };
        if (Array.isArray(child.material)) {
            child.material = child.material.map(m => applyRubber(m));
        } else {
            // @ts-ignore
            child.material = applyRubber(child.material);
        }
    }
    // 3. Square Pen Body - Matte with polygonOffset to sit BEHIND print area faces
    else if (product?.id === 'square-pen' && lowerName.includes('body')) {
        const applyMatte = (mat: any) => {
            const newMat = mat.clone();
            newMat.metalness = 0.0;
            newMat.roughness = 0.9;
            newMat.envMapIntensity = 1.0;
            newMat.side = THREE.DoubleSide;
            newMat.color.setHex(0xffffff);
            newMat.map = null;
            // Push body BEHIND face meshes to prevent seam gap bleed-through
            newMat.polygonOffset = true;
            newMat.polygonOffsetFactor = 1;  // Positive = push away from camera
            newMat.polygonOffsetUnits = 1;
            return newMat;
        };
        if (Array.isArray(child.material)) {
            child.material = child.material.map(m => applyMatte(m));
        } else {
            // @ts-ignore
            child.material = applyMatte(child.material);
        }
    }
    // 3b. Square Pen Faces - Matte (no polygonOffset, sits on top of body)
    else if (product?.id === 'square-pen' && lowerName.includes('face') && !lowerName.includes('all_faces')) {
        const applyMatte = (mat: any) => {
            const newMat = mat.clone();
            newMat.metalness = 0.0;
            newMat.roughness = 0.9;
            newMat.envMapIntensity = 1.0;
            newMat.side = THREE.DoubleSide;
            newMat.color.setHex(0xffffff);
            newMat.map = null;
            newMat.polygonOffset = false;
            newMat.polygonOffsetFactor = 0;
            newMat.polygonOffsetUnits = 0;
            return newMat;
        };
        if (Array.isArray(child.material)) {
            child.material = child.material.map(m => applyMatte(m));
        } else {
            // @ts-ignore
            child.material = applyMatte(child.material);
        }
    }
};

/**
 * Applies Fabric material to Cap
 */
export const applyCapMaterial = (
    child: THREE.Mesh,
    product: Product | undefined
) => {
    if (product?.id !== 'cap') return;
    if (child.userData.isPrintArea) return;

    const applyFabric = (mat: any) => {
        const newMat = mat.clone();
        newMat.metalness = 0.0;
        newMat.roughness = 0.9;
        newMat.envMapIntensity = 0.5;
        newMat.side = THREE.FrontSide;
        return newMat;
    };

    if (Array.isArray(child.material)) {
        child.material = child.material.map(m => applyFabric(m));
    } else {
        // @ts-ignore
        child.material = applyFabric(child.material);
    }
};

/**
 * Applies Jar materials (Clear Glass Body + Waxy Candy)
 */
export const applyJarMaterials = (
    child: THREE.Mesh,
    product: Product | undefined,
    grainTexture: THREE.Texture | null
) => {
    if (product?.id !== 'jar') return;

    const lowerName = child.name.toLowerCase();

    // 1. Glass Body
    const isJarBody = !child.userData.isPrintArea &&
        !lowerName.includes('cap') &&
        !lowerName.includes('jelly') &&
        !lowerName.includes('bean') &&
        (lowerName.includes('jar') || lowerName.includes('glass') || lowerName.includes('body') || lowerName.includes('bottle'));

    if (isJarBody) {
        const applyClearGlass = () => {
            return new THREE.MeshPhysicalMaterial({
                name: 'RealGlass',
                color: 0xffffff,
                metalness: 0.0,
                roughness: 0.0,
                transmission: 0.6,
                thickness: 4.0,
                ior: 1.5,
                transparent: true,
                opacity: 0.2,
                envMapIntensity: 1.5,
                clearcoat: 0.5,
                clearcoatRoughness: 0.0,
                side: THREE.FrontSide,
                depthWrite: true,
            });
        };

        if (Array.isArray(child.material)) {
            // @ts-ignore
            child.material = child.material.map(() => applyClearGlass());
        } else {
            child.material = applyClearGlass();
        }
        child.castShadow = false;
    }

    // 2. Jelly Beans
    const isJellyBean = lowerName.includes('jelly') || lowerName.includes('bean');
    if (isJellyBean) {
        const applyCandyFinish = (mat: any) => {
            const newMat = new THREE.MeshStandardMaterial({
                color: mat.color,
                map: mat.map,
                metalness: 0.0,
                roughness: 0.6,
                bumpMap: grainTexture,
                bumpScale: 0.1,
                transparent: false,
                opacity: 1.0,
                envMapIntensity: 0.5,
                side: THREE.FrontSide,
            });
            return newMat;
        };

        if (Array.isArray(child.material)) {
            child.material = child.material.map(m => applyCandyFinish(m));
        } else {
            // @ts-ignore
            child.material = applyCandyFinish(child.material);
        }
    }
};

/**
 * General cleanup or default properties
 */
export const applyDefaultProperties = (child: THREE.Mesh) => {
    // Disable shadows as requested project-wide
    child.castShadow = false;
    child.receiveShadow = false;

    // Force FrontSide generally 
    if (child.material) {
        if (Array.isArray(child.material)) {
            child.material.forEach(m => { m.side = THREE.FrontSide; });
        } else {
            child.material.side = THREE.FrontSide;
        }
    }
};
